import React, { useEffect, useRef } from 'react';
import './BackgroundFX.css';

const BackgroundFX = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let animationFrame;
    let width = 0;
    let height = 0;
    let pixelRatio = 1;
    let particles = [];
    let stars = [];
    let sparks = [];
    const mouse = {
      x: window.innerWidth * 0.72,
      y: window.innerHeight * 0.32,
      targetX: window.innerWidth * 0.72,
      targetY: window.innerHeight * 0.32,
      lastX: window.innerWidth * 0.72,
      lastY: window.innerHeight * 0.32,
      velocityX: 0,
      velocityY: 0,
      active: false,
    };

    const palette = [
      'rgba(125, 92, 255, 0.95)',
      'rgba(69, 132, 255, 0.9)',
      'rgba(25, 183, 255, 0.82)',
      'rgba(185, 142, 255, 0.75)',
    ];

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * pixelRatio;
      canvas.height = height * pixelRatio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

      const starCount = reduceMotion ? 130 : Math.min(320, Math.max(160, Math.floor(width / 4.8)));
      const particleCount = reduceMotion ? 82 : Math.min(250, Math.max(120, Math.floor(width / 7)));
      stars = Array.from({ length: starCount }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 1.2 + 0.2,
        alpha: Math.random() * 0.45 + 0.2,
        phase: Math.random() * Math.PI * 2,
      }));

      particles = Array.from({ length: particleCount }, (_, index) => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.34,
        vy: (Math.random() - 0.5) * 0.34,
        size: Math.random() * 2 + 0.9,
        glow: Math.random() * 12 + 9,
        phase: Math.random() * Math.PI * 2,
        color: palette[index % palette.length],
      }));
    };

    const handlePointerMove = (event) => {
      mouse.targetX = event.clientX;
      mouse.targetY = event.clientY;
      mouse.velocityX = mouse.targetX - mouse.lastX;
      mouse.velocityY = mouse.targetY - mouse.lastY;
      mouse.lastX = mouse.targetX;
      mouse.lastY = mouse.targetY;
      mouse.active = true;

      if (reduceMotion) {
        return;
      }

      const speed = Math.min(20, Math.hypot(mouse.velocityX, mouse.velocityY));
      if (speed > 1.2 && sparks.length < 150) {
        for (let index = 0; index < 3; index += 1) {
          sparks.push({
            x: mouse.targetX + (Math.random() - 0.5) * 18,
            y: mouse.targetY + (Math.random() - 0.5) * 18,
            vx: -mouse.velocityX * 0.018 + (Math.random() - 0.5) * 1.2,
            vy: -mouse.velocityY * 0.018 + (Math.random() - 0.5) * 1.2,
            life: 1,
            size: Math.random() * 2.8 + 1.1,
            color: palette[Math.floor(Math.random() * palette.length)],
          });
        }
      }
    };

    const handlePointerLeave = () => {
      mouse.active = false;
    };

    const drawNebula = () => {
      const gradient = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, Math.min(width, 760));
      gradient.addColorStop(0, 'rgba(125, 92, 255, 0.24)');
      gradient.addColorStop(0.34, 'rgba(25, 183, 255, 0.12)');
      gradient.addColorStop(1, 'rgba(7, 11, 26, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    };

    const drawStars = (time) => {
      stars.forEach((star) => {
        const twinkle = star.alpha + Math.sin(time * 0.0015 + star.phase) * 0.18;
        ctx.beginPath();
        ctx.shadowBlur = 8;
        ctx.shadowColor = 'rgba(190, 205, 255, 0.8)';
        ctx.fillStyle = `rgba(225, 235, 255, ${Math.max(0.08, twinkle)})`;
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const drawCursorAura = () => {
      if (!mouse.active) return;

      ctx.beginPath();
      ctx.shadowBlur = 38;
      ctx.shadowColor = 'rgba(125, 92, 255, 0.95)';
      ctx.strokeStyle = 'rgba(125, 92, 255, 0.3)';
      ctx.lineWidth = 1.35;
      ctx.arc(mouse.x, mouse.y, 62, 0, Math.PI * 2);
      ctx.stroke();

      ctx.beginPath();
      ctx.shadowBlur = 22;
      ctx.shadowColor = 'rgba(25, 183, 255, 0.95)';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
      ctx.arc(mouse.x, mouse.y, 2.4, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawSparks = () => {
      sparks = sparks.filter((spark) => spark.life > 0.02);

      sparks.forEach((spark) => {
        spark.x += spark.vx;
        spark.y += spark.vy;
        spark.vx *= 0.975;
        spark.vy *= 0.975;
        spark.life *= 0.94;

        ctx.beginPath();
        ctx.shadowBlur = 22 * spark.life;
        ctx.shadowColor = spark.color;
        ctx.fillStyle = spark.color.replace(/[\d.]+\)$/, `${0.62 * spark.life})`);
        ctx.arc(spark.x, spark.y, spark.size * spark.life, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const animate = () => {
      const time = Date.now();
      ctx.clearRect(0, 0, width, height);
      mouse.x += (mouse.targetX - mouse.x) * (reduceMotion ? 0.06 : 0.2);
      mouse.y += (mouse.targetY - mouse.y) * (reduceMotion ? 0.06 : 0.2);
      drawNebula();
      drawStars(time);

      particles.forEach((particle, index) => {
        const dx = particle.x - mouse.x;
        const dy = particle.y - mouse.y;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        const influence = Math.max(0, 1 - distance / 370);
        const drift = Math.sin(time * 0.0018 + particle.phase) * 0.24;
        const orbitForce = mouse.active && !reduceMotion ? influence * 0.86 : 0;
        const repelForce = mouse.active ? influence * (reduceMotion ? 0.68 : 3.55) : influence * 0.58;

        particle.vx += (dx / distance) * repelForce * 0.018 + (-dy / distance) * orbitForce * 0.018;
        particle.vy += (dy / distance) * repelForce * 0.018 + (dx / distance) * orbitForce * 0.018;
        particle.vx *= 0.988;
        particle.vy *= 0.988;
        particle.x += particle.vx + drift;
        particle.y += particle.vy + Math.cos(time * 0.001 + particle.phase) * 0.09;

        if (particle.x < -20) particle.x = width + 20;
        if (particle.x > width + 20) particle.x = -20;
        if (particle.y < -20) particle.y = height + 20;
        if (particle.y > height + 20) particle.y = -20;

        const alpha = mouse.active ? 0.48 + influence * 0.5 : 0.36;
        ctx.beginPath();
        ctx.shadowBlur = particle.glow + influence * 18;
        ctx.shadowColor = particle.color;
        ctx.fillStyle = particle.color.replace(/[\d.]+\)$/, `${alpha})`);
        ctx.arc(particle.x, particle.y, particle.size + influence * 1.3, 0, Math.PI * 2);
        ctx.fill();

        if (mouse.active && distance < 320) {
          ctx.beginPath();
          ctx.shadowBlur = 0;
          ctx.strokeStyle = `rgba(117, 130, 255, ${0.28 * (1 - distance / 320)})`;
          ctx.lineWidth = 1;
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.stroke();
        }

        for (let otherIndex = index + 1; otherIndex < particles.length; otherIndex += 1) {
          const other = particles[otherIndex];
          const lineDx = particle.x - other.x;
          const lineDy = particle.y - other.y;
          const lineDistance = Math.sqrt(lineDx * lineDx + lineDy * lineDy);

          if (lineDistance < 95) {
            ctx.beginPath();
            ctx.shadowBlur = 0;
            ctx.strokeStyle = `rgba(117, 130, 255, ${0.09 * (1 - lineDistance / 95)})`;
            ctx.lineWidth = 1;
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(other.x, other.y);
            ctx.stroke();
          }
        }
      });

      drawSparks();
      drawCursorAura();
      animationFrame = requestAnimationFrame(animate);
    };

    resize();
    animate();
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="background-fx" aria-hidden="true" />;
};

export default BackgroundFX;
